import React from "react";
import {
  Box,
  Button,
  Typography,
  IconButton,
  Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { SubmitBundleModal } from "./SubmitBundleModal/SubmitBundleModal";
import { useTransactionsView } from "./useTransactionsView";
import { TradesAccordion } from "./TradesAccordion";
import { FXSpotAccordion } from "./FXSpotAccordion";
import { WireTransferAccordion } from "./WireTransferAccordion";
import { PendingTransactionsSection } from "./PendingTransactionsSection";

export const TransactionsView: React.FC = () => {
  const {
    setSubView,
    clients,
    custodians,
    instruments,
    pendingTransactions,
    filteredTransactions,
    savedGeneratedDocuments,
    error,
    clearError,
    expandedSections,
    handleAccordionChange,
    tradeRows,
    handleTradeChange,
    handleAddTradeRow,
    handleRemoveTradeRow,
    handleReAddTradeRow,
    fxRows,
    handleFXChange,
    handleAddFXRow,
    handleRemoveFXRow,
    handleReAddFXRow,
    wireRows,
    handleWireChange,
    handleAddWireRow,
    handleRemoveWireRow,
    handleReAddWireRow,
    clientFilter,
    setClientFilter,
    custodianFilter,
    setCustodianFilter,
    typeFilter,
    setTypeFilter,
    statusFilter,
    setStatusFilter,
    handleClearAll,
    handleValidate,
    handleSubmitAll,
    deleteTransaction,
    submitBundleModalOpen,
    selectedBundleTransactions,
    selectedCustodianName,
    selectedCustodianEmail,
    matchedFlowForBundle,
    handleOpenSubmitBundle,
    handleCloseSubmitBundle,
    handleSendEmail,
    getTypeLabel,
  } = useTransactionsView();

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 2 }}>
        <IconButton onClick={() => setSubView("menu")} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6">Bulk Transaction Entry</Typography>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="text"
          onClick={handleClearAll}
          sx={{ textTransform: "uppercase", fontWeight: 500 }}
        >
          Clear All
        </Button>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleValidate}
          disabled={pendingTransactions.length === 0}
          sx={{ textTransform: "uppercase", fontWeight: 500 }}
        >
          Validate
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmitAll}
          disabled={pendingTransactions.filter((t) => t.status === "validated").length === 0}
          sx={{ textTransform: "uppercase", fontWeight: 500 }}
        >
          Submit All
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={clearError} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Accordion Sections */}
      <Box sx={{ flex: 1, overflow: "auto" }}>
        <TradesAccordion
          expanded={expandedSections.trades}
          onAccordionChange={handleAccordionChange("trades")}
          tradeRows={tradeRows}
          clients={clients}
          custodians={custodians}
          instruments={instruments}
          handleTradeChange={handleTradeChange}
          handleAddTradeRow={handleAddTradeRow}
          handleRemoveTradeRow={handleRemoveTradeRow}
          handleReAddTradeRow={handleReAddTradeRow}
        />

        <FXSpotAccordion
          expanded={expandedSections.fxspot}
          onAccordionChange={handleAccordionChange("fxspot")}
          fxRows={fxRows}
          clients={clients}
          custodians={custodians}
          handleFXChange={handleFXChange}
          handleAddFXRow={handleAddFXRow}
          handleRemoveFXRow={handleRemoveFXRow}
          handleReAddFXRow={handleReAddFXRow}
        />

        <WireTransferAccordion
          expanded={expandedSections.wire}
          onAccordionChange={handleAccordionChange("wire")}
          wireRows={wireRows}
          clients={clients}
          custodians={custodians}
          handleWireChange={handleWireChange}
          handleAddWireRow={handleAddWireRow}
          handleRemoveWireRow={handleRemoveWireRow}
          handleReAddWireRow={handleReAddWireRow}
        />

        <PendingTransactionsSection
          filteredTransactions={filteredTransactions}
          clients={clients}
          custodians={custodians}
          clientFilter={clientFilter}
          setClientFilter={setClientFilter}
          custodianFilter={custodianFilter}
          setCustodianFilter={setCustodianFilter}
          typeFilter={typeFilter}
          setTypeFilter={setTypeFilter}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          handleOpenSubmitBundle={handleOpenSubmitBundle}
          deleteTransaction={deleteTransaction}
          getTypeLabel={getTypeLabel}
        />
      </Box>

      {/* Submit Bundle Modal */}
      <SubmitBundleModal
        open={submitBundleModalOpen}
        onClose={handleCloseSubmitBundle}
        transactions={selectedBundleTransactions}
        matchedFlow={matchedFlowForBundle}
        custodianName={selectedCustodianName}
        custodianEmail={selectedCustodianEmail}
        generatedDocuments={savedGeneratedDocuments}
        onSend={handleSendEmail}
      />
    </Box>
  );
};
